import { QueryClient } from '@tanstack/react-query';
import { getFriendlyErrorMessage } from '../utils/errorUtils';

/**
 * Configure the global query client for React Query
 * This handles caching, retries, and error handling
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Default caching behavior
      staleTime: 60 * 1000, // 1 minute before refetching
      cacheTime: 5 * 60 * 1000, // 5 minutes in cache
      
      // Default retry behavior
      retry: 1, // Retry failed requests once
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
      
      // Default error handling
      onError: (err) => {
        // Log all query errors
        console.error('Query error:', getFriendlyErrorMessage(err), err);
      },
      
      // Default refetch behaviors
      refetchOnWindowFocus: false, // Don't refresh data when window regains focus
      refetchOnReconnect: true, // Refresh when reconnecting after offline
    },
    mutations: {
      // Default retry behavior for mutations
      retry: 0, // Don't retry mutations by default
      
      // Default error handling
      onError: (err) => {
        // Log all mutation errors
        console.error('Mutation error:', getFriendlyErrorMessage(err), err);
      },
    },
  },
});

/**
 * React Query key factory to standardize how we define and organize cache keys
 */
export const queryKeys = {
  // Project keys
  projects: {
    all: ['projects'],
    detail: (id) => ['projects', 'detail', id],
  },
  
  // Knowledge base keys
  knowledgeBases: {
    all: ['knowledgeBases'],
    detail: (id) => ['knowledgeBases', 'detail', id],
  },
  
  // Document keys
  documents: {
    all: ['documents'],
    byKb: (kbId) => ['documents', 'kb', kbId],
    detail: (id) => ['documents', 'detail', id],
    progress: (id) => ['documents', 'progress', id],
  },
  
  // Dialog keys
  dialogs: {
    all: ['dialogs'],
    detail: (id) => ['dialogs', 'detail', id],
  },
  
  // Conversation keys
  conversations: {
    all: ['conversations'],
    byDialog: (dialogId) => ['conversations', 'dialog', dialogId],
    detail: (id) => ['conversations', 'detail', id],
  },
};

/**
 * Invalidate the cache for a specific entity and its related collections
 */
export function invalidateQueries(entityType, id) {
  switch (entityType) {
    case 'project':
      // Invalidate the specific project and the projects list
      queryClient.invalidateQueries(queryKeys.projects.detail(id));
      queryClient.invalidateQueries(queryKeys.projects.all);
      break;
      
    case 'kb':
      // Invalidate the specific KB, the KBs list, and related documents
      queryClient.invalidateQueries(queryKeys.knowledgeBases.detail(id));
      queryClient.invalidateQueries(queryKeys.knowledgeBases.all);
      queryClient.invalidateQueries(queryKeys.documents.byKb(id));
      break;
      
    case 'document':
      // Invalidate the specific document
      queryClient.invalidateQueries(queryKeys.documents.detail(id));
      queryClient.invalidateQueries(queryKeys.documents.progress(id));
      
      // If we know the KB ID, invalidate the KB's documents list
      const kbId = id.kbId;
      if (kbId) {
        queryClient.invalidateQueries(queryKeys.documents.byKb(kbId));
      }
      break;
      
    case 'dialog':
      // Invalidate the specific dialog, the dialogs list, and related conversations
      queryClient.invalidateQueries(queryKeys.dialogs.detail(id));
      queryClient.invalidateQueries(queryKeys.dialogs.all);
      queryClient.invalidateQueries(queryKeys.conversations.byDialog(id));
      break;
      
    case 'conversation':
      // Invalidate the specific conversation
      queryClient.invalidateQueries(queryKeys.conversations.detail(id));
      
      // If we know the dialog ID, invalidate the dialog's conversations list
      const dialogId = id.dialogId;
      if (dialogId) {
        queryClient.invalidateQueries(queryKeys.conversations.byDialog(dialogId));
      }
      break;
      
    default:
      console.warn(`Unknown entity type for cache invalidation: ${entityType}`);
  }
} 